import { Download, FileText, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeletePartDocument,
  useUploadPartDocument,
  usePartDocuments,
} from '@/features/part-storage/queries';
import { partStorageApi } from '@/features/part-storage/service';
import {
  ALLOWED_DOC_TYPES,
  DOC_MAX_BYTES,
  PART_DOCUMENT_KINDS,
  PART_QUOTA_BYTES,
  type PartDocument,
  type PartDocumentKind,
} from '@/features/part-storage/types';
import { ApiError } from '@/lib/api';

interface PartDocumentsCardProps {
  partId: string;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function PartDocumentsCard({ partId }: PartDocumentsCardProps) {
  const { t } = useTranslation();
  const { data: docs, isLoading } = usePartDocuments(partId);
  const uploadMut = useUploadPartDocument(partId);
  const deleteMut = useDeletePartDocument(partId);

  const [kind, setKind] = useState<PartDocumentKind>('DATASHEET');
  const [progress, setProgress] = useState<{ name: string; pct: number } | null>(
    null,
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorParams, setErrorParams] = useState<Record<string, unknown>>();
  const [pendingDelete, setPendingDelete] = useState<PartDocument | null>(
    null,
  );

  const handleFiles = async (files: File[]) => {
    setErrorKey(null);
    for (const file of files) {
      setProgress({ name: file.name, pct: 0 });
      try {
        await uploadMut.mutateAsync({
          file,
          kind,
          onProgress: (loaded, total) =>
            setProgress({
              name: file.name,
              pct: total > 0 ? Math.round((loaded / total) * 100) : 0,
            }),
        });
      } catch (err) {
        if (err instanceof ApiError) {
          setErrorKey(err.key);
          setErrorParams(err.params);
        } else {
          setErrorKey('part.documents.uploadError');
        }
        break;
      } finally {
        setProgress(null);
      }
    }
  };

  const handleDelete = (doc: PartDocument) => {
    deleteMut.mutate(doc.id, {
      onSuccess: () => setPendingDelete(null),
      onError: (err) => {
        setPendingDelete(null);
        if (err instanceof ApiError) {
          setErrorKey(err.key);
          setErrorParams(err.params);
        } else {
          setErrorKey('part.documents.deleteError');
        }
      },
    });
  };

  const handleDownload = async (doc: PartDocument) => {
    setErrorKey(null);
    try {
      const { url } = await partStorageApi.getDocumentDownloadUrl(
        partId,
        doc.id,
      );
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorKey(err.key);
        setErrorParams(err.params);
      } else {
        setErrorKey('part.documents.uploadError');
      }
    }
  };

  const totalBytes = (docs ?? []).reduce((sum, d) => sum + d.sizeBytes, 0);
  const groupedByKind = (docs ?? []).reduce<
    Record<PartDocumentKind, PartDocument[]>
  >(
    (acc, d) => {
      (acc[d.kind] ??= []).push(d);
      return acc;
    },
    {} as Record<PartDocumentKind, PartDocument[]>,
  );

  return (
    <>
      <Card>
        <CardHeader className="space-y-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText aria-hidden className="h-4 w-4" />
            {t('part.documents.title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('part.documents.quota', {
              used: humanSize(totalBytes),
              total: humanSize(PART_QUOTA_BYTES),
            })}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorKey && (
            <Alert variant="destructive">
              <AlertTitle>{t('part.documents.uploadError')}</AlertTitle>
              <AlertDescription>
                {t(errorKey, {
                  defaultValue: t('part.documents.uploadError'),
                  ...(errorParams ?? {}),
                })}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <FileDropzone
              accept={ALLOWED_DOC_TYPES.join(',')}
              maxSizeBytes={DOC_MAX_BYTES}
              maxFiles={5}
              disabled={uploadMut.isPending}
              label={
                progress ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {t('part.documents.uploadingProgress', {
                      name: progress.name,
                      percent: progress.pct,
                    })}
                  </span>
                ) : (
                  t('part.documents.uploadHint')
                )
              }
              onFilesSelected={handleFiles}
              onError={(e) => {
                setErrorKey(e.key);
                setErrorParams(e.params);
              }}
            />
            <div className="flex flex-col gap-1 sm:w-44">
              <label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="part-doc-kind"
              >
                {t('part.documents.kindSelectLabel')}
              </label>
              <select
                id="part-doc-kind"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={kind}
                onChange={(e) => setKind(e.target.value as PartDocumentKind)}
                disabled={uploadMut.isPending}
              >
                {PART_DOCUMENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`part.documents.kindLabel.${k}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (docs ?? []).length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t('part.documents.empty')}
            </div>
          ) : (
            <ul className="space-y-3">
              {PART_DOCUMENT_KINDS.flatMap((k) => {
                const list = groupedByKind[k] ?? [];
                if (list.length === 0) return [];
                return [
                  <li key={`${k}-header`} className="pt-1">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t(`part.documents.kindLabel.${k}`)}
                    </h4>
                  </li>,
                  ...list.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm"
                    >
                      <FileText
                        aria-hidden
                        className="h-5 w-5 text-muted-foreground"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{doc.fileName}</p>
                        <p className="text-xs text-muted-foreground">
                          {humanSize(doc.sizeBytes)} ·{' '}
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download aria-hidden className="h-4 w-4" />
                          <span className="sr-only sm:not-sr-only">
                            {t('part.documents.download')}
                          </span>
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setPendingDelete(doc)}
                          disabled={deleteMut.isPending}
                        >
                          <Trash2 aria-hidden className="h-4 w-4" />
                          <span className="sr-only sm:not-sr-only">
                            {t('part.documents.delete')}
                          </span>
                        </Button>
                      </div>
                    </li>
                  )),
                ];
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title={t('part.documents.confirmDeleteTitle')}
        description={t('part.documents.confirmDeleteDescription', {
          name: pendingDelete?.fileName ?? '',
        })}
        confirmLabel={t('part.documents.delete')}
        variant="destructive"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
      />
    </>
  );
}
